'use client';

/**
 * AI chat 에 담은 소재 — 무엇을 담았나 · 담기/빼기/비우기.
 *
 * 카드를 **통째로** 쥔다 — 담아 둔 소재가 그리드에서 사라져도(탭·검색어를 바꿔도) 서랍의 줄과 대화가 남는다.
 * 세션에 담는다(`SEARCH_ALL_AI_CART_KEY`) — 탭을 닫으면 사라진다.
 */

import type { AiChatAskItem } from 'src/actions/search-all-ai-chat';

import { useMemo, useState, useEffect, useCallback } from 'react';

import { SEARCH_ALL_AI_CART_KEY } from 'src/constants/search-all-session';

import type { MediaCard } from '../utils/search-all-media-cards';

/** 한 번에 담을 수 있는 수 — 라우트도 같은 값으로 거른다 */
export const AI_CART_MAX = 12;

export function useSearchAllAiCart() {
  const [cards, setCards] = useState<MediaCard[]>([]);

  // 붙은 뒤 한 번 — 서버 렌더에는 저장소가 없다
  useEffect(() => {
    setCards(readCart());
  }, []);

  const save = useCallback((next: MediaCard[]) => {
    setCards(next);
    try {
      sessionStorage.setItem(SEARCH_ALL_AI_CART_KEY, JSON.stringify(next));
    } catch {
      // 저장이 막힌 브라우저 — 이번 화면에서는 담긴 채로 쓴다
    }
  }, []);

  const toggle = useCallback((card: MediaCard) => {
    setCards((prev) => {
      const next = prev.some((c) => c.key === card.key)
        ? prev.filter((c) => c.key !== card.key)
        : [...prev, card].slice(0, AI_CART_MAX);
      try {
        sessionStorage.setItem(SEARCH_ALL_AI_CART_KEY, JSON.stringify(next));
      } catch {
        // 위와 같다
      }
      return next;
    });
  }, []);

  const remove = useCallback((key: string) => save(cards.filter((c) => c.key !== key)), [cards, save]);
  const clear = useCallback(() => save([]), [save]);

  const keys = useMemo(() => new Set(cards.map((c) => c.key)), [cards]);
  const items = useMemo(() => cards.map(toAskItem).filter((item): item is AiChatAskItem => item !== null), [cards]);

  return { cards, keys, items, full: cards.length >= AI_CART_MAX, toggle, remove, clear };
}

/** 카드 → 서버가 원본을 찾는 데 필요한 것만. 못 찾을 카드면 `null` */
export function toAskItem(card: MediaCard): AiChatAskItem | null {
  if (card.kind === 'ad') return card.ad?.id ? { kind: 'ad', id: card.ad.id } : null;
  if (card.kind === 'organic') {
    const id = typeof card.post?.id === 'string' ? card.post.id : '';
    return id ? { kind: 'post', id } : null;
  }
  const product = card.product;
  return product?.postId ? { kind: 'product', id: product.postId, source: product.source, rankKind: product.kind } : null;
}

// ---- 이하 비공개 ----------------------------------------------------------

function readCart(): MediaCard[] {
  try {
    const raw = sessionStorage.getItem(SEARCH_ALL_AI_CART_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? (parsed as MediaCard[]).slice(0, AI_CART_MAX) : [];
  } catch {
    return [];
  }
}
